import React, { Suspense, lazy, useEffect } from "react";
import PropTypes from "prop-types";
import { render } from "react-dom";
import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import { Provider, useSelector, useDispatch } from "react-redux";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "./firebase";
import { store } from "./store";
import { setAuth, setProfile, clearAuth } from "./store/slices/authSlice";
import { isLoaded, isEmpty } from "./store/hooks";

import { Loading } from "UIComponent";

import WelcomePage from "./component/layout/WelcomePage";
const HomePage = lazy(() => import("./component/layout/home/HomePage.js"));
const LoginPage = lazy(() => import("./component/layout/LoginPage.js"));
const TermsPage = lazy(() => import("./component/layout/TermsPage.js"));
const FormPage = lazy(() => import("./component/layout/FormPage.js"));
const PublicFormPage = lazy(() => import("./component/layout/PublicFormPage.js"));
const PrivacyPage = lazy(() => import("./component/layout/PrivacyPage.js"));
const FeedbackPage = lazy(() => import("./component/layout/FeedbackPage.js"));

function AuthListener({ children }) {
  const dispatch = useDispatch();

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        dispatch(
          setAuth({
            uid: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
            displayName: user.displayName,
            photoURL: user.photoURL,
          })
        );

        // Listen to user profile
        const unsubscribeProfile = onSnapshot(
          doc(db, "users", user.uid),
          (doc) => {
            if (doc.exists()) {
              dispatch(setProfile(doc.data()));
            } else {
              dispatch(setProfile(null));
            }
          }
        );

        return () => unsubscribeProfile();
      } else {
        dispatch(clearAuth());
      }
    });

    return () => unsubscribeAuth();
  }, [dispatch]);

  return children;
}

AuthListener.propTypes = {
  children: PropTypes.node,
};

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated or if auth is not
// yet loaded
function PrivateRoute({ children, ...rest }) {
  const authState = useSelector((state) => state.auth.auth);

  return (
    <Route
      {...rest}
      render={({ location }) => {
        if (!isLoaded(authState)) {
          return <Loading />;
        }
        const isAuthenticated = isLoaded(authState) && !isEmpty(authState);
        return isAuthenticated ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: "/login",
              state: { from: location },
            }}
          />
        );
      }}
    />
  );
}

PrivateRoute.propTypes = {
  children: PropTypes.node,
};

function PublicRoute({ children, ...rest }) {
  return (
    <Route
      {...rest}
      render={() => {
        return children;
      }}
    />
  );
}

PublicRoute.propTypes = {
  children: PropTypes.node,
};

function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        <Route path="/privacy_policy">
          <PrivacyPage />
        </Route>
        <Route path="/terms_and_conditions">
          <TermsPage />
        </Route>
        <Route path="/feedback">
          <FeedbackPage />
        </Route>
        <Route path="/login">
          <LoginPage />
        </Route>
        <PrivateRoute path="/home">
          <HomePage />
        </PrivateRoute>
        <PublicRoute path="/userform">
          <FormPage />
        </PublicRoute>
        <PublicRoute path="/p/:type/:id">
          <PublicFormPage />
        </PublicRoute>
        <PublicRoute exact path="/">
          <WelcomePage />
        </PublicRoute>
      </Switch>
    </Suspense>
  );
}

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("./firebase-messaging-sw.js")
      .then(function (registration) {
        console.log("Registration successful, scope is:", registration.scope);
      })
      .catch(function (err) {
        console.log("Service worker registration failed, error:", err);
      });
  });
}

// We need to add the root here since HtmlWebpackPlugin
// doesn't automatically add a root div
function appComponent() {
  const element = document.createElement("div");
  element.setAttribute("id", "root");
  element.style.height = "100%";
  return element;
}
document.body.appendChild(appComponent());

render(
  <Provider store={store}>
    <AuthListener>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </AuthListener>
  </Provider>,
  document.getElementById("root")
);

